import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight, Search, MapPin, Shield, Star, Sparkles, Wifi, Utensils, ShowerHead,
  Building2, Users, GraduationCap, Wallet, Phone, MessageCircle, ChevronRight,
  CheckCircle2, Zap, Heart, Trophy, BookOpen, Coffee
} from "lucide-react";
import api from "../lib/api";
import PropertyCard from "../components/PropertyCard";

const POPULAR_CITIES = [
  { name: "Jaipur", img: "https://images.unsplash.com/photo-1599661046289-e31897846e41?w=800", count: "120+ PGs" },
  { name: "Bengaluru", img: "https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=800", count: "240+ PGs" },
  { name: "Pune", img: "https://images.unsplash.com/photo-1572455024976-2dabd5ee4cd9?w=800", count: "180+ PGs" },
  { name: "Delhi", img: "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=800", count: "210+ PGs" },
  { name: "Hyderabad", img: "https://images.unsplash.com/photo-1572468305764-78b2b9b9d6f0?w=800", count: "160+ PGs" },
  { name: "Mumbai", img: "https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?w=800", count: "150+ PGs" },
];

const TRUST_LOGOS = ["IIT Bangalore", "Poornima University", "DU", "BITS Pilani", "NIT Jaipur", "JECRC"];

const SERVICES = [
  { i: Building2, t: "Verified PGs", d: "360° room tours, real reviews, zero brokerage. Single, double, or shared — your call.", c: "emerald" },
  { i: Utensils, t: "Food Delivery", d: "Daily tiffin menus from local kitchens. Rate every meal. Pay monthly or per order.", c: "coral" },
  { i: ShowerHead, t: "Laundry Pickup", d: "Schedule pickup like Zomato. Wash, iron, dry-clean. Live status updates.", c: "blue" },
  { i: Sparkles, t: "Essentials Store", d: "Buckets, locks, bedsheets, study lamps. Move-in ready combos delivered.", c: "gold" },
  { i: Shield, t: "Secure Payments", d: "UPI, cards, net-banking. Auto rent reminders, GST invoices, receipts in app.", c: "emerald" },
  { i: MessageCircle, t: "24×7 Support", d: "AI chatbot + human escalation. Tickets, masked chat, emergency button.", c: "coral" },
];

const STUDENT_FEATURES = [
  "Verified PG listings with 360° room tours",
  "Live food menus & meal rating",
  "Secure rent payments + electricity bills",
  "In-app chat & ticket system",
  "Auto rent reminders",
  "Roommate finder",
  "Community wall & leaderboards",
  "Digital document verification & e-agreement",
];

const OWNER_FEATURES = [
  "Real-time occupancy dashboard",
  "Auto rent collection & invoicing",
  "One-click onboarding of existing students",
  "Auto vacancy updater",
  "Parcel locker notifications with OTP",
  "Photo scheduler & FAQ bot",
  "Revenue tracker + commission ledger",
  "Premium listing & promotions",
];

const TESTIMONIALS = [
  { n: "Priya S.", c: "Student · IIT Jaipur", t: "Hostro ne pura PG search 2 din mein finish kara diya. Verified listings + 360° tour ne trust banaya.", r: 5 },
  { n: "Rohit M.", c: "PG Owner · Malviya Nagar", t: "Rent collection ab automatic hai. Mujhe har month ka revenue dashboard milta hai. Game changer.", r: 5 },
  { n: "Ananya V.", c: "Student · Poornima Univ.", t: "Khana, laundry, rent — sab ek app mein. Roommate bhi yahin se mila!", r: 5 },
];

export default function Landing() {
  const nav = useNavigate();
  const [city, setCity] = useState("Jaipur");
  const [stats, setStats] = useState({ properties: 0, students: 0, owners: 0, cities: 0 });
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/stats").then(r => setStats(r.data)).catch(()=>{});
    api.get("/properties", { params: { limit: 6 } }).then(r => setFeatured(r.data)).catch(()=>{});
  }, []);

  const submit = (e) => { e.preventDefault(); nav(`/properties?city=${encodeURIComponent(city)}`); };

  const tagColor = (c) => ({
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    coral:   "bg-rose-50 text-rose-700 border-rose-200",
    blue:    "bg-blue-50 text-blue-700 border-blue-200",
    gold:    "bg-amber-50 text-amber-700 border-amber-200",
  })[c] || "bg-gray-50 text-gray-700 border-gray-200";

  return (
    <div className="bg-[#FBFAF6]" data-testid="landing-page">
      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Soft gradient backdrop */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/60 via-[#FBFAF6] to-rose-50/40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-12 md:pt-20 pb-16 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-xs font-semibold uppercase tracking-wider" data-testid="hero-eyebrow">
              <Sparkles size={14}/> India&apos;s smartest living + PG management platform
            </div>
            <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl mt-5 text-[#0E1F18]">
              Find a PG that<br/>
              <span className="relative inline-block">
                <span className="relative z-10 text-emerald-600">actually</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-emerald-200/70 -z-0"></span>
              </span><br/>
              feels like home.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#4A4A4A] max-w-xl">
              Verified PGs, hostels, co-living & apartments — for students, working professionals & families.
              Plus a full PMS for owners: rent collection, occupancy, complaints, visitor entry.
              One Platform. Endless Possibilities.
            </p>

            {/* Search bar — modern pill style */}
            <form onSubmit={submit} className="mt-8 bg-white rounded-2xl border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-2 flex flex-col sm:flex-row gap-2 max-w-xl" data-testid="hero-search-form">
              <div className="flex-1 flex items-center gap-2 px-4">
                <MapPin size={18} className="text-emerald-600"/>
                <input
                  list="hero-cities"
                  className="w-full bg-transparent py-3 outline-none font-medium text-[#0E1F18] placeholder:text-gray-400"
                  placeholder="Enter city or college..."
                  value={city}
                  onChange={(e)=>setCity(e.target.value)}
                  data-testid="hero-search-input"
                />
                <datalist id="hero-cities">
                  {POPULAR_CITIES.map(c => <option key={c.name} value={c.name}/>)}
                </datalist>
              </div>
              <button className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-xl transition" type="submit" data-testid="hero-search-btn">
                <Search size={16}/> Search PGs
              </button>
            </form>

            <div className="mt-5 flex flex-wrap gap-2" data-testid="hero-cities-pills">
              <span className="text-xs font-semibold text-gray-500 self-center mr-1">Popular:</span>
              {POPULAR_CITIES.slice(0,5).map(c => (
                <button key={c.name} onClick={()=>nav(`/properties?city=${c.name}`)} className="px-3 py-1.5 rounded-full bg-white border border-gray-200 text-sm font-medium hover:border-emerald-400 hover:text-emerald-700 transition" data-testid={`city-pill-${c.name}`}>{c.name}</button>
              ))}
            </div>

            {/* Mini trust row */}
            <div className="mt-8 flex items-center gap-6 flex-wrap">
              <div className="flex items-center -space-x-2">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-9 h-9 rounded-full border-2 border-white bg-gradient-to-br from-emerald-300 to-rose-300"></div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i=>(<Star key={i} size={14} fill="#F4B942" stroke="none"/>))}
                  <span className="ml-1 text-sm font-bold text-[#0E1F18]">4.8/5</span>
                </div>
                <div className="text-xs text-gray-500">from 1,200+ student reviews</div>
              </div>
            </div>
          </div>

          {/* Hero visual card */}
          <div className="relative" data-testid="hero-collage">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-gray-200 shadow-[0_20px_60px_rgb(0,0,0,0.08)]">
              <img src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200" alt="PG room" className="w-full h-full object-cover"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"/>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="bg-white/95 backdrop-blur rounded-xl p-4 border border-white/40">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Verified</span>
                    <span className="text-xs font-bold uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">Girls</span>
                  </div>
                  <div className="font-display font-black text-lg leading-tight">Aurora Girls Hostel · Malviya Nagar</div>
                  <div className="flex items-center justify-between mt-2">
                    <div>
                      <div className="text-xs text-gray-500">starts from</div>
                      <div className="font-display font-black text-2xl">₹8,500<span className="text-xs font-bold text-gray-500">/mo</span></div>
                    </div>
                    <div className="flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded-full text-xs font-bold">
                      <Star size={12} fill="currentColor"/> 4.9
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating live-listings card */}
            <div className="absolute -top-4 -right-4 bg-white rounded-2xl border border-gray-200 shadow-lg p-4 w-44" data-testid="floating-listings">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Live listings</div>
              <div className="font-display font-black text-3xl text-emerald-600 mt-1">{stats.properties || "—"}+</div>
              <div className="text-xs text-gray-500 mt-1">across {stats.cities || "—"} cities</div>
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl border border-gray-200 shadow-lg p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 grid place-items-center"><Shield size={20} className="text-emerald-600"/></div>
              <div>
                <div className="text-xs font-bold text-[#0E1F18]">Verified owners</div>
                <div className="text-[10px] text-gray-500">KYC + Document checked</div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="bg-white border-y border-gray-200 py-5" data-testid="trust-strip">
          <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Trusted by students from</span>
            {TRUST_LOGOS.map(l => (
              <span key={l} className="font-display font-bold text-sm text-gray-700">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ============== STATS ============== */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16" data-testid="stats-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "properties", l: "PG Listings", v: stats.properties, suffix: "+" },
            { k: "students", l: "Happy Students", v: stats.students, suffix: "+" },
            { k: "owners", l: "PG Owners", v: stats.owners, suffix: "+" },
            { k: "cities", l: "Cities", v: stats.cities, suffix: "+" },
          ].map(s => (
            <div key={s.k} className="bg-white rounded-2xl border border-gray-200 p-6 hover:border-emerald-300 hover:shadow-lg transition" data-testid={`stat-${s.k}`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{s.l}</div>
              <div className="font-display font-black text-5xl text-[#0E1F18] mt-2">{(s.v || 0).toLocaleString()}<span className="text-emerald-600">{s.suffix}</span></div>
            </div>
          ))}
        </div>
      </section>

      {/* ============== SERVICES (What we offer) ============== */}
      <section className="bg-gradient-to-b from-white to-emerald-50/30 border-y border-gray-200 py-20" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl">
            <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold uppercase tracking-wider">All-in-one platform</span>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-4 text-[#0E1F18]">
              Everything a student needs.<br/>
              <span className="text-emerald-600">In one app.</span>
            </h2>
            <p className="mt-3 text-lg text-gray-600">From finding a PG to ordering dinner to scheduling laundry — Hostro runs your whole student life.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {SERVICES.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 hover:border-emerald-300 hover:shadow-xl hover:-translate-y-1 transition group" data-testid={`service-${i}`}>
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl border ${tagColor(s.c)} mb-4 group-hover:scale-110 transition`}>
                  <s.i size={22}/>
                </div>
                <h3 className="font-display font-black text-xl text-[#0E1F18]">{s.t}</h3>
                <p className="mt-2 text-gray-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== POPULAR CITIES ============== */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20" data-testid="cities-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="px-3 py-1 rounded-full bg-rose-100 text-rose-800 text-xs font-semibold uppercase tracking-wider">Across India</span>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-3 text-[#0E1F18]">Browse by city</h2>
          </div>
          <Link to="/properties" className="inline-flex items-center gap-1 text-emerald-700 font-semibold hover:gap-2 transition" data-testid="all-cities-link">
            View all <ChevronRight size={16}/>
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {POPULAR_CITIES.map(c => (
            <Link key={c.name} to={`/properties?city=${c.name}`} className="group relative aspect-[4/5] rounded-2xl overflow-hidden border border-gray-200 hover:border-emerald-400 transition" data-testid={`city-card-${c.name}`}>
              <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition duration-500"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"/>
              <div className="absolute bottom-3 left-3 right-3 text-white">
                <div className="font-display font-black text-xl">{c.name}</div>
                <div className="text-xs opacity-90">{c.count}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============== FEATURED PROPERTIES ============== */}
      <section className="bg-white border-y border-gray-200 py-20" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-wider">Hand-picked</span>
              <h2 className="font-display font-black text-4xl md:text-5xl mt-3 text-[#0E1F18]">Featured PGs near you</h2>
            </div>
            <Link to="/properties" className="inline-flex items-center gap-2 bg-[#0E1F18] hover:bg-emerald-700 text-white font-semibold px-5 py-3 rounded-xl transition" data-testid="see-all-btn">
              See all <ArrowRight size={16}/>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.length > 0
              ? featured.map(p => <PropertyCard key={p.id} p={p}/>)
              : [1,2,3].map(i=>(<div key={i} className="aspect-[4/3] rounded-2xl border border-gray-200 bg-gray-50 animate-pulse"/>))}
          </div>
        </div>
      </section>

      {/* ============== TWO-COLUMN: For Students vs For Owners ============== */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20" data-testid="audience-deep">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Students */}
          <div className="bg-gradient-to-br from-emerald-50 to-white rounded-3xl border border-emerald-200 p-8 md:p-10" data-testid="for-students">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold uppercase tracking-wider mb-4">
              <GraduationCap size={14}/> For Students
            </div>
            <h3 className="font-display font-black text-3xl md:text-4xl text-[#0E1F18]">Your student life,<br/>simplified.</h3>
            <ul className="mt-6 space-y-2.5">
              {STUDENT_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2.5 text-[#2a2a2a]">
                  <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0 mt-0.5"/>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/register" className="inline-flex items-center gap-2 mt-7 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3 rounded-xl transition" data-testid="cta-student">
              Sign up as a student <ArrowRight size={16}/>
            </Link>
          </div>

          {/* Owners */}
          <div className="bg-gradient-to-br from-rose-50 to-white rounded-3xl border border-rose-200 p-8 md:p-10" data-testid="for-owners">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-800 text-xs font-semibold uppercase tracking-wider mb-4">
              <Building2 size={14}/> For PG Owners
            </div>
            <h3 className="font-display font-black text-3xl md:text-4xl text-[#0E1F18]">Run your PG<br/>like a business.</h3>
            <ul className="mt-6 space-y-2.5">
              {OWNER_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2.5 text-[#2a2a2a]">
                  <CheckCircle2 size={18} className="text-rose-600 flex-shrink-0 mt-0.5"/>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/list-property" className="inline-flex items-center gap-2 mt-7 bg-rose-600 hover:bg-rose-700 text-white font-semibold px-5 py-3 rounded-xl transition" data-testid="cta-owner">
              List your PG free <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section className="bg-[#0E1F18] text-white py-20" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl">
            <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs font-semibold uppercase tracking-wider">How it works</span>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-4">
              From search to move-in<br/>in <span className="text-emerald-400">under 5 minutes</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5 mt-12">
            {[
              { i: Search, t: "Search & filter", d: "Pick city, college, budget, gender. See real photos, reviews, amenities." },
              { i: BookOpen, t: "Tour & verify", d: "360° room tours, verified owner docs, masked chat with the owner." },
              { i: Wallet, t: "Book securely", d: "Pay token + security deposit via UPI/cards. Auto-receipt + e-agreement." },
              { i: Heart, t: "Move in & relax", d: "Auto rent reminders, food, laundry, tickets — all from one dashboard." },
            ].map((s,i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur" data-testid={`step-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-emerald-500 grid place-items-center text-[#0E1F18]"><s.i size={20}/></div>
                <div className="text-xs font-semibold uppercase tracking-wider text-emerald-300 mt-4">Step {i+1}</div>
                <h3 className="font-display font-black text-2xl mt-1">{s.t}</h3>
                <p className="text-gray-300 mt-2 text-sm">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== TESTIMONIALS ============== */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20" data-testid="testimonials-section">
        <div className="text-center max-w-2xl mx-auto">
          <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold uppercase tracking-wider">Loved by 5,000+</span>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-3 text-[#0E1F18]">What our community says</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl transition" data-testid={`testimonial-${i}`}>
              <div className="flex gap-0.5">
                {[...Array(t.r)].map((_,j)=>(<Star key={j} size={14} fill="#F4B942" stroke="none"/>))}
              </div>
              <p className="mt-3 text-[#2a2a2a] leading-relaxed">&ldquo;{t.t}&rdquo;</p>
              <div className="mt-5 flex items-center gap-3 pt-4 border-t border-gray-100">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 grid place-items-center text-white font-bold">{t.n[0]}</div>
                <div>
                  <div className="font-bold text-[#0E1F18]">{t.n}</div>
                  <div className="text-xs text-gray-500">{t.c}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============== ECOSYSTEM (Vendors + Colleges) ============== */}
      <section className="bg-gradient-to-r from-emerald-50 via-white to-rose-50 border-y border-gray-200 py-20" data-testid="ecosystem-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-3xl border border-gray-200 p-8" data-testid="for-vendors">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold uppercase tracking-wider mb-4">
              <Coffee size={14}/> For Vendors
            </div>
            <h3 className="font-display font-black text-3xl text-[#0E1F18]">Cooks, laundries & cafés — go digital.</h3>
            <p className="text-gray-600 mt-3">Manage menus, accept orders, track deliveries, get paid commission-free.</p>
            <Link to="/vendors/register" className="inline-flex items-center gap-2 mt-5 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-3 rounded-xl transition" data-testid="cta-vendor">
              Join as vendor <ArrowRight size={16}/>
            </Link>
          </div>
          <div className="bg-white rounded-3xl border border-gray-200 p-8" data-testid="for-colleges">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-wider mb-4">
              <Trophy size={14}/> For Colleges
            </div>
            <h3 className="font-display font-black text-3xl text-[#0E1F18]">Welfare panel for student housing.</h3>
            <p className="text-gray-600 mt-3">Onboard incoming students, monitor PG safety, share scholarships & internships.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-5 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-3 rounded-xl transition" data-testid="cta-college">
              Partner with us <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24" data-testid="cta-section">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 p-10 md:p-16 text-center">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-rose-300/20 rounded-full blur-3xl"></div>
          <div className="relative">
            <Zap size={48} className="mx-auto text-amber-300 mb-4"/>
            <h2 className="font-display font-black text-4xl md:text-6xl text-white leading-none">Ready to move in?</h2>
            <p className="text-lg md:text-xl text-white/90 mt-5 max-w-xl mx-auto">Create a free Hostro account — save PGs, send enquiries, list your property — all in 60 seconds.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-white text-[#0E1F18] font-bold px-7 py-4 rounded-xl hover:bg-gray-100 transition" data-testid="cta-register">
                Create free account <ArrowRight size={16}/>
              </Link>
              <Link to="/properties" className="inline-flex items-center justify-center gap-2 bg-[#0E1F18] hover:bg-black text-white font-bold px-7 py-4 rounded-xl transition" data-testid="cta-browse">
                Browse PGs
              </Link>
            </div>
            <div className="mt-6 flex items-center justify-center gap-2 text-white/80 text-sm">
              <Phone size={14}/> Need help? Call <a href="tel:+918905290804" className="font-bold underline">+91 89052 90804</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
